import i18n from '@i18n/index';

import { loadIdentity } from './identity-cache';

import type { AccessControlProvider } from '@refinedev/core';

type ActionMap = Partial<Record<string, string[]>>;

/**
 * Маппинг (resource, action) → один или несколько permission-имён, любого из которых
 * достаточно для разрешения.
 *
 * Имена прав совпадают с серверной моделью (`uz.beantech.p2ploans.rbac.Permissions`).
 * Если на бэке меняется набор — обновляем `@shared/constants/permissions.ts`
 * и эту карту синхронно.
 */
const matrix: Record<string, ActionMap> = {
  settings: {
    list: ['settings.read', 'settings.write'],
    show: ['settings.read', 'settings.write'],
    edit: ['settings.write'],
    update: ['settings.write'],
  },
  agreements: {
    list: ['agreement.read.all'],
    show: ['agreement.read.all'],
  },
  'fee-tariffs': {
    list: ['settings.read', 'settings.write'],
    edit: ['settings.write'],
    update: ['settings.write'],
  },
  documents: {
    upload: ['settings.write'],
  },
  users: {
    list: ['users.read', 'users.manage'],
    show: ['users.read', 'users.manage'],
    edit: ['users.manage'],
    create: ['users.manage'],
    delete: ['users.manage'],
  },
  admins: {
    list: ['users.read', 'users.manage'],
    create: ['users.manage'],
  },
  roles: {
    list: ['roles.read'],
    create: ['roles.write'],
    edit: ['roles.write'],
    delete: ['roles.write'],
  },
  permissions: {
    list: ['permissions.read'],
  },
  devices: {
    list: ['device.read'],
    delete: ['device.revoke'],
  },
};

/**
 * Access-control провайдер Refine.
 *
 * Гейтит UI по permission-именам бэкенда (например `settings.write`), не по ролям.
 * Если для пары `(resource, action)` нет правила в `matrix` — разрешаем (provider
 * остаётся открытым по умолчанию; ужесточаем явно).
 *
 * Permissions берутся из общего `identity-cache` (см. `identity-cache.ts`) —
 * тот же кэш, что использует `authProvider.getIdentity/getPermissions`. Один
 * `/api/users/me` на сессию.
 */
export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }) => {
    if (!resource) return { can: true };
    const required = matrix[resource]?.[action];
    if (!required || required.length === 0) return { can: true };
    const me = await loadIdentity();
    const permissions = me?.permissions ?? [];
    const allowed = required.some((p) => permissions.includes(p));
    return allowed
      ? { can: true }
      : { can: false, reason: i18n.t('common:access.denied', { permissions: required.join(', ') }) };
  },
  options: {
    buttons: {
      enableAccessControl: true,
      hideIfUnauthorized: true,
    },
  },
};
